import { inject, Injectable } from '@angular/core';
import {
  Auth,
  User as FirebaseUser,
  onIdTokenChanged,
} from '@angular/fire/auth';
import { AuthStateService } from './auth-state.service';
import { SessionExpiredError } from './session-expired.error';

/** Owns the Firebase session only; application authorization lives in AuthStateService. */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly auth = inject(Auth);
  private readonly authStateService = inject(AuthStateService);
  private firebaseUser: FirebaseUser | null = null;
  private readonly restoredSession: Promise<FirebaseUser | null>;

  constructor() {
    this.restoredSession = new Promise((resolve) => {
      let restored = false;
      onIdTokenChanged(this.auth, (user) => {
        this.firebaseUser = user;
        if (!user) {
          this.authStateService.clear();
        }
        if (!restored) {
          restored = true;
          resolve(user);
        }
      });
    });
  }

  public waitForInitialSession(): Promise<FirebaseUser | null> {
    return this.restoredSession;
  }

  public get currentUser(): FirebaseUser | null {
    return this.firebaseUser;
  }

  public async getCurrentToken(): Promise<string> {
    const user =
      this.auth.currentUser ??
      this.firebaseUser ??
      (await this.waitForInitialSession());
    if (!user) {
      throw new SessionExpiredError();
    }

    try {
      return await user.getIdToken();
    } catch (error) {
      if (isUnrecoverableFirebaseSessionError(error)) {
        await this.clearSession();
        throw new SessionExpiredError(undefined, error);
      }

      // A token refresh can fail simply because the browser is offline. Keep
      // Firebase and application state intact so the mounted form and its
      // values remain available when connectivity comes back.
      throw error;
    }
  }

  public async clearSession(): Promise<void> {
    this.authStateService.clear();
    try {
      await this.auth.signOut();
    } catch {
      // The application state is still cleared; callers must receive the
      // original session error instead of a secondary sign-out failure.
    }
  }
}

function isUnrecoverableFirebaseSessionError(error: unknown): boolean {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('code' in error) ||
    typeof error.code !== 'string'
  ) {
    return false;
  }

  return [
    'auth/invalid-user-token',
    'auth/user-disabled',
    'auth/user-not-found',
    'auth/user-token-expired',
  ].includes(error.code);
}
